






















































import { Component, Vue, Watch } from 'vue-property-decorator';
import { TIMESHEET_WORK_RELATIONS } from '@/queries/Timesheet';
import { GQL_timesheetWorkRelations_currentUnit_workRelations } from '@/queries/__generated__/timesheetWorkRelations';
import TimesheetComment from './timesheet-comment.vue';
import TimesheetContent from './timesheet-content.vue';
import permissionGuard from '@/guards/permission-guard';
import { isEmployedDuringPeriod, workRelationDisplay } from '@/app/WorkRelation';
import { formatRegisteredHours, registeredMinutes, sortByRegisteredHours } from '@/app/Timesheet';

@Component({
  components: {
    TimesheetComment,
    TimesheetContent,
  },
  beforeRouteEnter(to,from, next) {
    permissionGuard('canCreatePaychecks', to, from, next)
      .catch(router => {
        router.push({ name: 'Dashboard' });
        router.app.$sMessage.error(router.app.$t('noPermissions'));
      });
  },
})
export default class Timesheets extends Vue {
  private loadingWorkRelations = false;

  private workRelations: GQL_timesheetWorkRelations_currentUnit_workRelations[] = [];
  private selectedWorkRelationId: string | null = null;
  private selectedCalendarMonth = this.$dayjs().format('YYYY-MM');
  // Only the newest fetch may land; an older one for another month is dropped.
  private fetchCounter = 0;

  get calendarMonthOptions() {
    const months: string[] = [];
    const now = this.$dayjs().startOf('month');
    for (let i = 0; i < 24; i++) {
      months.push(now.subtract(i, 'month').format('YYYY-MM'));
    }
    return months;
  }

  get selectedWorkRelation(): GQL_timesheetWorkRelations_currentUnit_workRelations | null {
    if (!this.selectedWorkRelationId) return null;
    return this.visibleWorkRelations.find(wr => wr.id === this.selectedWorkRelationId)!;
  }

  get visibleWorkRelations() {
    const monthStart = this.$dayjs.strict(`${this.selectedCalendarMonth}-01`);
    const monthEnd = monthStart.endOf('month');

    const employed = this.workRelations.filter(wr => {
      return isEmployedDuringPeriod(
        wr,
        {startDate: monthStart.format('YYYY-MM-DD'), endDate: monthEnd.format('YYYY-MM-DD')},
      );
    });

    return sortByRegisteredHours(employed);
  }

  private async mounted() {
    if (this.workRelations.length) return;

    await this.fetchWorkRelations();
  }

  @Watch('selectedWorkRelationId')
  private selectionChanged() {
    this.ensureSelectedWorkRelationIsVisible();
  }

  @Watch('selectedCalendarMonth')
  private async monthChanged() {
    this.ensureSelectedWorkRelationIsVisible();
    // The registered hours in the dropdown belong to the selected month.
    await this.fetchWorkRelations();
    this.ensureSelectedWorkRelationIsVisible();
  }

  private async fetchWorkRelations({ silent = false } = {}) {
    const fetchNumber = ++this.fetchCounter;
    if (!silent) this.loadingWorkRelations = true;
    try {
      const res = await this.$apollo.query({
        query: TIMESHEET_WORK_RELATIONS,
        variables: { calendarMonth: this.selectedCalendarMonth },
        fetchPolicy: 'network-only',
      });
      if (fetchNumber !== this.fetchCounter) return;
      this.workRelations = res.data.currentUnit.workRelations;
    } catch (_err) {
      if (fetchNumber !== this.fetchCounter) return;
      this.workRelations = [];
    } finally {
      if (fetchNumber === this.fetchCounter) this.loadingWorkRelations = false;
    }
  }

  private workRelationLabel(wr: GQL_timesheetWorkRelations_currentUnit_workRelations) {
    return workRelationDisplay(wr, true);
  }

  private workRelationHours(wr: GQL_timesheetWorkRelations_currentUnit_workRelations): string {
    const minutes = registeredMinutes(wr);
    if (minutes <= 0) return this.$t('Timesheet.noHoursThisMonth') as string;

    return this.$t('Timesheet.hoursThisMonth', { hours: formatRegisteredHours(minutes) }) as string;
  }

  private handleTimesheetSaved() {
    // Refresh the hours shown in the dropdown without blanking the page.
    this.fetchWorkRelations({ silent: true });
  }

  private handleCommentSaved(comment: string) {
    if (!this.selectedWorkRelationId) return;

    const index = this.workRelations.findIndex(wr => wr.id === this.selectedWorkRelationId);
    if (index < 0) return;

    this.$set(this.workRelations, index, {
      ...this.workRelations[index],
      timesheetComment: comment,
    });
  }

  private ensureSelectedWorkRelationIsVisible() {
    const visibleIds = this.visibleWorkRelations.map(wr => wr.id);
    if (this.selectedWorkRelationId && visibleIds.includes(this.selectedWorkRelationId)) return;

    this.selectedWorkRelationId = null;
  }
}
